import React, { useState } from 'react';
import { ArrowRight, Building2, Eye, EyeOff, Loader2, LockKeyhole, User } from 'lucide-react';
import api from '../services/api';
import LicenseStatus from './LicenseStatus';
import AdminPasswordRecovery from './AdminPasswordRecovery';

function LoginPage({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { username, password });
      const authUser = response.data?.user || { login: response.data?.username || username };
      const authToken = response.data?.token;
      const permissions = response.data?.permissions || {};
      localStorage.setItem('authUser', authUser.login || username);
      localStorage.setItem('authUserData', JSON.stringify(authUser));
      localStorage.setItem('authPermissions', JSON.stringify(permissions));
      if (authToken) {
        localStorage.setItem('authToken', authToken);
      }
      if (process.env.REACT_APP_DISTRIBUTION === 'true' && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        await api.post('/remote/session');
      }
      onLoginSuccess({ user: authUser, permissions });
    } catch (requestError) {
      const message = requestError.response?.data?.detail || 'Falha ao entrar no sistema.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative isolate h-[100dvh] overflow-y-auto bg-[#0b1824]">
      <img
        src={`${process.env.PUBLIC_URL}/images/condominio-login.png`}
        alt=""
        className="pointer-events-none fixed inset-0 -z-20 h-full w-full object-cover object-[35%_center] lg:object-center"
      />
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[linear-gradient(90deg,rgba(5,17,28,0.76)_0%,rgba(5,17,28,0.32)_55%,rgba(5,17,28,0.48)_100%)]" />

      <div className="mx-auto flex min-h-full max-w-[1440px] flex-col px-5 py-7 sm:px-10 sm:py-9 lg:px-16">
        <header className="flex items-center gap-3 text-white">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/25 bg-white/10 backdrop-blur-md">
            <Building2 size={24} strokeWidth={1.5} aria-hidden="true" />
          </span>
          <div className="leading-tight">
            <p className="text-lg font-semibold tracking-tight">Control Condomínio</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.24em] text-white/70">Gestão e controle de acesso</p>
          </div>
        </header>

        <main className="grid flex-1 items-center gap-10 py-10 lg:grid-cols-[1fr_440px] lg:gap-16 lg:py-14">
          <section className="hidden max-w-xl text-white lg:block">
            <p className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-medium tracking-wide backdrop-blur-md">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-300" />
              Seu condomínio, conectado
            </p>
            <h1 className="text-5xl font-semibold leading-[1.12] tracking-tight xl:text-6xl">
              Mais controle.<br />
              <span className="text-teal-200">Mais tranquilidade.</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/80">
              Pessoas, acessos e a rotina do seu condomínio em um só lugar.
              Tudo ao alcance de quem cuida.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/20 pt-6 text-xs font-medium text-white/75">
              <span>Moradores e visitantes</span>
              <span>Entregas e reservas</span>
              <span>Controle de acesso</span>
            </div>
          </section>

          <section aria-labelledby="login-heading" className="mx-auto w-full max-w-[440px] rounded-[28px] border border-white/70 bg-white/95 p-7 shadow-[0_24px_80px_rgba(0,0,0,0.3)] backdrop-blur-xl sm:p-10">
            <div className="mb-8">
              <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                <LockKeyhole size={23} strokeWidth={1.7} aria-hidden="true" />
              </span>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-700">Bem-vindo ao seu painel</p>
              <h2 id="login-heading" className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">Entrar no sistema</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">Use suas credenciais para acessar o condomínio.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="login-username" className="mb-2 block text-sm font-medium text-slate-700">Usuário</label>
                <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50/80 px-4 transition focus-within:border-teal-600 focus-within:bg-white focus-within:ring-4 focus-within:ring-teal-600/10">
                  <User size={18} className="shrink-0 text-slate-400" aria-hidden="true" />
                  <input
                    id="login-username"
                    type="text"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    className="min-w-0 w-full bg-transparent px-3 py-3.5 text-sm text-slate-900 outline-none"
                    placeholder="Digite seu usuário"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="login-password" className="mb-2 block text-sm font-medium text-slate-700">Senha</label>
                <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50/80 pl-4 pr-2 transition focus-within:border-teal-600 focus-within:bg-white focus-within:ring-4 focus-within:ring-teal-600/10">
                  <LockKeyhole size={18} className="shrink-0 text-slate-400" aria-hidden="true" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="min-w-0 w-full bg-transparent px-3 py-3.5 text-sm text-slate-900 outline-none"
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    aria-pressed={showPassword}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-xl bg-teal-700 px-5 py-4 text-sm font-semibold text-white shadow-lg shadow-teal-900/15 transition hover:bg-teal-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-600/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : null}
                {loading ? 'Entrando...' : 'Entrar no painel'}
                {!loading && <ArrowRight size={18} aria-hidden="true" />}
              </button>
            </form>

            <AdminPasswordRecovery />

            <LicenseStatus className="mt-6" />

            <p className="mt-7 border-t border-slate-200 pt-5 text-center text-xs leading-relaxed text-slate-400">
              Acesso exclusivo para usuários autorizados.
            </p>
          </section>
        </main>

        <footer className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/60">
          <span>Control Condomínio</span>
          <span>Uma gestão mais simples. Uma rotina mais tranquila.</span>
        </footer>
      </div>
    </div>
  );
}

export default LoginPage;
